import { useRef, useState } from "react";

type RequestKey = string | number;

export function useRequestLock() {
  const lockedKeysRef = useRef(new Set<string>());
  // State snapshots trigger UI renders; the ref remains authoritative within a tick.
  const [, setPendingKeys] = useState(new Set<string>());

  const isLocked = (key: RequestKey) =>
    lockedKeysRef.current.has(String(key));

  const runLocked = async <T,>(
    key: RequestKey,
    task: () => Promise<T>,
  ): Promise<T | undefined> => {
    const normalizedKey = String(key);

    if (lockedKeysRef.current.has(normalizedKey)) {
      return undefined;
    }

    lockedKeysRef.current.add(normalizedKey);
    setPendingKeys(new Set(lockedKeysRef.current));

    try {
      return await task();
    } finally {
      lockedKeysRef.current.delete(normalizedKey);
      setPendingKeys(new Set(lockedKeysRef.current));
    }
  };

  return { isLocked, runLocked };
}
